import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Grid} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {Box} from '@mui/system';
import type {NoProps} from 'types';
import {Concept} from 'types';
import type {
  CustomFilterType,
  FilterSet,
  Filter as FilterType,
} from 'services/advancedSearch/types';
import type {CodeListId} from 'services/codelists/types';
import {isFeatureEnabled} from 'configuration';
import {useLocalization} from 'localization';
import {
  excludeBokbasenFilter,
  excludeInternationalFilter,
} from 'services/advancedSearch';
import {useCreateFilterSetMutation} from 'services/advancedSearch/advancedSearchApi';
import {CodeListsGate, useAllCodeListIds} from 'services/codelists';
import {unique} from 'services/utils';
import {Card, IconButton, Layout, Spacer, Text, useSnacks} from 'components';
import {ActionButtonV2} from 'components/button/ActionButtonV2';
import {Table} from 'schema';
import {advancedSearchTableConfig} from 'schemas';
import {
  Filter,
  FilterSets,
  HelpTooltip,
  SingleColumnSelectionAccordion,
} from './components';
import {CustomFilter} from './components/CustomFilter';
import {SelectViewMode} from './components/SelectViewMode';
import {StartBulkUpdate} from './components/StartBulkUpdate';
import {useAdvancedSearchContext} from './context/AdvancedSearchContext';
import {findNextFilter} from './functions';
import {isNotCustomFilter} from './functions/assertAsCustomFilterType';
import {
  useColumnSets,
  useCreateFilterDialog,
  useDownloadDialog,
  useFilterSets,
} from './hooks';
import {useDownloadDistinctDialog} from './hooks/useDownloadDistinctDialog';

type Props = NoProps;

export const AdvancedSearch: React.FC<Props> = () => {
  const {t} = useLocalization();
  const {columnSets} = useColumnSets();
  const {
    currentColumns,
    currentFilters,
    addCurrentFilter,
    setCurrentFilters,
    availableFilters,
    loading,
    onSearch,
    onDownload,
    dataResponse,
    schema,
    loadingSearch,
    loadingDownload,
    allColumns,
    viewMode,
    setViewMode,
  } = useAdvancedSearchContext();

  /**
   * Dynamically load unknown codelists.
   */
  const allExistingCodelistIds = useAllCodeListIds();
  const codelistIds = useMemo(() => {
    const dynamic = availableFilters.map(a => a.codelistId) as CodeListId[];
    return unique([...allExistingCodelistIds, ...dynamic].filter(c => c));
  }, [allExistingCodelistIds, availableFilters]);

  const [createFilterSet] = useCreateFilterSetMutation();
  const {CreateFilterDialog, openCreateFilterDialog} =
    useCreateFilterDialog(createFilterSet);

  const {DownloadCsvDialog, openDownloadCsvDialog} = useDownloadDialog(
    columnSets,
    allColumns,
    currentColumns,
    onDownload,
  );

  const {DownloadDistinctCsvDialog, openDownloadDistinctCsvDialog} =
    useDownloadDistinctDialog(
      columnSets,
      allColumns,
      currentColumns,
      allColumns.filter(a => a.supportsDistinct),
      onDownload,
    );

  const {editFilterSet} = useFilterSets();
  const {successSnack} = useSnacks();
  const [editingFilterSet, setEditingFilterSet] = useState<FilterSet | null>(
    null,
  );
  const [preEditFilters, setPreEditFilters] = useState<
    Array<FilterType | CustomFilterType>
  >([]);

  const onStartEdit = useCallback(
    (fs: FilterSet) => {
      if (!editingFilterSet) {
        setPreEditFilters(fs.filters);
      }
      setEditingFilterSet(fs);
      setCurrentFilters(fs.filters);
    },
    [editingFilterSet, setCurrentFilters],
  );

  const onCancelEdit = useCallback(() => {
    if (preEditFilters) {
      setCurrentFilters(preEditFilters);
    }
    setEditingFilterSet(null);
    setPreEditFilters([]);
  }, [preEditFilters, setCurrentFilters]);

  const onSaveEdit = useCallback(async () => {
    if (!editingFilterSet) return;
    await editFilterSet({
      id: editingFilterSet.id,
      name: editingFilterSet.name,
      isPublic: editingFilterSet.isPublic,
      filters: currentFilters,
      columns: [],
    });
    successSnack('Lagrede søk oppdatert');
    setEditingFilterSet(null);
    setPreEditFilters([]);
  }, [currentFilters, editFilterSet, editingFilterSet, successSnack]);

  const handleAddCurrentFilter = useCallback(() => {
    addCurrentFilter(findNextFilter(currentFilters, availableFilters));
  }, [addCurrentFilter, availableFilters, currentFilters]);

  const handleResetCurrentFilters = useCallback(() => {
    // Empty current filters and add one so the filter list is not empty.
    setCurrentFilters([findNextFilter([], availableFilters)]);
  }, [setCurrentFilters, availableFilters]);

  useEffect(() => {
    if (currentFilters.filter(isNotCustomFilter).length === 0) {
      handleAddCurrentFilter();
    }
  }, [currentFilters, handleAddCurrentFilter]);

  const tableConfig = useMemo(() => {
    return {
      ...advancedSearchTableConfig,
      pinnedColumns: {
        left: viewMode === Concept.work ? ['workId'] : ['ean'],
      },
    };
  }, [viewMode]);

  if (loading || !codelistIds) {
    return (
      <Card>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <CodeListsGate codeLists={codelistIds}>
      <Card>
        <Grid
          container
          spacing={2}
          rowSpacing={2}
          sx={{
            flex: 1,
            width: '100%',
          }}>
          <Grid size={{xs: 6, sm: 3}}>
            <FilterSets
              onStartEdit={onStartEdit}
              editingFilterSetId={editingFilterSet?.id ?? null}
            />
          </Grid>
          <Grid size={{xs: 6, sm: 9}}>
            <Layout flex={1} horizontal pb={1}>
              <Grid
                container
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}>
                <Grid size={{xs: 1}}>
                  <HelpTooltip />
                </Grid>
                <Grid
                  size={{xs: 10}}
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}>
                  <CustomFilter
                    label={t('data.filter.custom.international.label')}
                    filter={excludeInternationalFilter}
                  />
                  <CustomFilter
                    label={t('data.filter.custom.bokbasen.label')}
                    filter={excludeBokbasenFilter}
                  />
                </Grid>
              </Grid>
            </Layout>
            {currentFilters.map(f => (
              <Filter key={f.id} filter={f} />
            ))}

            <Layout flex={1} horizontal pb={1}>
              <Grid container sx={{width: '100%'}}>
                <Grid
                  size={{sm: 3}}
                  sx={{
                    pr: 1,
                  }}>
                  <Layout alignItems={'flex-start'}>
                    <IconButton
                      postText={
                        <Text kind="primary" variant={'button'}>
                          {t('page.search.filter.add')}
                        </Text>
                      }
                      size={'small'}
                      icon={'Add'}
                      onClick={handleAddCurrentFilter}
                    />
                    {editingFilterSet ? (
                      <Layout>
                        <Layout horizontal>
                          <IconButton
                            postText={
                              <Text kind="primary" variant={'button'}>
                                {t('page.search.filter.update')}
                              </Text>
                            }
                            size={'small'}
                            icon={'StarSmall'}
                            onClick={onSaveEdit}
                          />
                          <Spacer width={1} />
                          <IconButton
                            postText={
                              <Text kind="primary" variant={'button'}>
                                {t('general.cancel')}
                              </Text>
                            }
                            size={'small'}
                            icon={'CancelEdit'}
                            onClick={onCancelEdit}
                          />
                        </Layout>
                      </Layout>
                    ) : (
                      <IconButton
                        postText={
                          <Text kind="primary" variant={'button'}>
                            {t('page.search.filter.save')}
                          </Text>
                        }
                        size={'small'}
                        icon={'StarSmall'}
                        onClick={() => openCreateFilterDialog(currentFilters)}
                      />
                    )}
                    {CreateFilterDialog}
                  </Layout>
                </Grid>
                <Grid
                  size={{sm: 3}}
                  sx={{
                    pr: 1,
                  }}>
                  <IconButton
                    postText={
                      <Text kind="primary" variant={'button'}>
                        {t('page.search.filter.removeAll')}
                      </Text>
                    }
                    size={'small'}
                    icon={'Remove'}
                    onClick={handleResetCurrentFilters}
                  />
                </Grid>
                <Grid size={{sm: 5}}>
                  <Layout horizontal justifyContent={'flex-end'}>
                    {isFeatureEnabled('downloadDistinct') ? (
                      <>
                        <IconButton
                          postText={
                            <Text
                              kind={loadingDownload ? 'secondary' : 'primary'}
                              variant={'button'}>
                              {t(
                                'page.search.downloadDistrinct.download.title',
                              )}
                            </Text>
                          }
                          icon={'Download'}
                          disabled={loadingDownload}
                          onClick={openDownloadDistinctCsvDialog}
                        />
                        {DownloadDistinctCsvDialog}
                      </>
                    ) : null}
                    <IconButton
                      postText={
                        <Text
                          kind={loadingDownload ? 'secondary' : 'primary'}
                          variant={'button'}>
                          {t('page.search.filter.download.title')}
                        </Text>
                      }
                      icon={'Download'}
                      disabled={loadingDownload}
                      onClick={() => openDownloadCsvDialog()}
                    />
                    {DownloadCsvDialog}
                    <Spacer width={3} />
                    <ActionButtonV2
                      icon={'Search'}
                      title={t('general.search')}
                      onClick={() => onSearch(true)}
                      disabled={loadingSearch}
                      showSpinnerOnClick={true}
                    />
                  </Layout>
                </Grid>
              </Grid>
            </Layout>
          </Grid>
        </Grid>
      </Card>
      <Spacer height={2} />
      <SingleColumnSelectionAccordion />
      <Table
        loading={loadingSearch}
        schema={schema}
        configuration={tableConfig}
        data={dataResponse.rows}
        rowCount={dataResponse.hits}
        header={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}>
            <Box>
              <Text kind="primary" variant={'body1'}>
                {t('page.search.hits', {count: dataResponse.hits})}
              </Text>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}>
              <SelectViewMode viewMode={viewMode} onChange={setViewMode} />
              <StartBulkUpdate />
            </Box>
          </Box>
        }
      />
    </CodeListsGate>
  );
};
